import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { NEW_LOOK_GAP } from "common/NewLook/tokens.ts";
import { AFFILIATION_CONTAINER } from "features/affiliation/NewLook/newLookUtils";

interface TabLayoutProps {
  /** The tab's figures (TabSummary). */
  summary: ReactNode;
  /** The desktop table panel (TablePanel). */
  table?: ReactNode;
  /** The phone list that replaces the table (RowList). */
  list?: ReactNode;
  /** A panel next to the table (a chart, payout methods). */
  aside?: ReactNode;
  /** Puts the aside above the table instead of next to it. */
  asideOnTop?: boolean;
  "data-testid"?: string;
}

/**
 * The new-look affiliation tab: its summary on top, then the table (the
 * page's main content) with an optional panel beside it. Phones get rows
 * instead of the table and the aside above them.
 */
const TabLayout = ({
  summary,
  table,
  list,
  aside,
  asideOnTop,
  "data-testid": testId,
}: TabLayoutProps) => {
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  // The header search filters the phone lists.
  usePageWrapper({ showSearch: true });

  const main = isBottomNavigator ? list : table;
  const asideFirst = isBottomNavigator || asideOnTop;

  return (
    <Wrapper data-testid={testId}>
      {summary}
      <Body $withAside={!!aside && !asideFirst}>
        {asideFirst && aside}
        {main && <Main>{main}</Main>}
        {!asideFirst && aside}
      </Body>
    </Wrapper>
  );
};

export default TabLayout;

/** Content width from which the aside sits next to the table. */
const ASIDE_BREAKPOINT = 980;

const Wrapper = styled.div`
  container: ${AFFILIATION_CONTAINER} / inline-size;
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_GAP}px;
  /* Phones: the 12px rhythm of the phone page. */
  @media (max-width: 600px) {
    gap: 12px;
  }
`;
const Body = styled.div<{ $withAside: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: ${NEW_LOOK_GAP}px;
  @media (max-width: 600px) {
    gap: 12px;
  }
  ${(p) =>
    p.$withAside &&
    css`
      @container ${AFFILIATION_CONTAINER} (min-width: ${ASIDE_BREAKPOINT}px) {
        grid-template-columns: minmax(0, 1fr) minmax(300px, 340px);
      }
    `}
`;
const Main = styled.div`
  min-width: 0;
`;
